import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box } from "@mui/material";
import { UserStatusCell } from "Components/Users/UserListTable/UserStatusCell";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import type { HunterListDto } from "Infrastructure/Api/Api";
import { formatDate } from "Utils/DateUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { appUrl } from "Utils/UrlUtils";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { Resources, useResource, type TFunction } from "Translations/Resources";

const RESOURCES = Resources.Hunter.HuntersOverview;

const createColumns = (t: TFunction): Array<MRT_ColumnDef<HunterListDto>> => [
  {
    id: "prerequisites",
    accessorFn: ({ completedPrerequisitesCount, totalPrerequisitesCount }) =>
      completedPrerequisitesCount >= totalPrerequisitesCount,
    header: t(RESOURCES.TableHeaders.Prerequisites),
    size: 60,
    Cell: ({ row }) => {
      const { completedPrerequisitesCount, totalPrerequisitesCount } =
        row.original;
      const isComplete = completedPrerequisitesCount >= totalPrerequisitesCount;
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {isComplete ? (
            <CheckCircleOutlineIcon color="success" fontSize="small" />
          ) : (
            <CancelOutlinedIcon
              color={completedPrerequisitesCount > 0 ? "warning" : "error"}
              fontSize="small"
            />
          )}
          {completedPrerequisitesCount}/{totalPrerequisitesCount}
        </Box>
      );
    },
    filterVariant: "select",
    filterSelectOptions: [
      { label: t(Resources.Common.Yes), value: "Completed" },
      { label: t(Resources.Common.No), value: "Incomplete" },
    ],
    enableSorting: false,
  },
  {
    accessorKey: "login",
    header: t(RESOURCES.TableHeaders.Login),
    size: 200,
  },
  {
    accessorKey: "party.firstName",
    header: t(RESOURCES.TableHeaders.FirstName),
    size: 150,
  },
  {
    accessorKey: "party.lastName",
    header: t(RESOURCES.TableHeaders.LastName),
    size: 150,
  },
  {
    accessorKey: "party.companyName",
    header: t(RESOURCES.TableHeaders.CompanyName),
    size: 200,
  },
  {
    id: "affiliatedTo",
    accessorFn: ({ affiliatedToName, affiliatedToEmail }) =>
      affiliatedToName || affiliatedToEmail || "",
    header: t(RESOURCES.TableHeaders.AffiliatedTo),
    size: 200,
    // Server-side filter on the attributed publisher (login / name). Sorting stays off:
    // the value is resolved after pagination and is not a sortable entity column.
    enableSorting: false,
    Cell: ({ row }) => {
      const { affiliatedToName, affiliatedToEmail } = row.original;
      if (!affiliatedToName && !affiliatedToEmail) {
        return "-";
      }
      return affiliatedToName
        ? `${affiliatedToName} (${affiliatedToEmail})`
        : affiliatedToEmail;
    },
  },
  {
    id: "dateCreated",
    accessorFn: ({ dateCreated }) => dateCreated,
    header: t(RESOURCES.TableHeaders.DateCreated),
    size: 170,
    filterVariant: "date",
    Cell: ({ row }) => formatDate(row.original.dateCreated, true),
  },
  {
    accessorKey: "status",
    header: t(RESOURCES.TableHeaders.Status),
    Cell: ({ row }) => <UserStatusCell status={row.original.status} />,
    filterVariant: "multi-select",
    filterSelectOptions: [
      { label: t(Resources.Form.UserStatus.Active), value: "Active" },
      { label: t(Resources.Form.UserStatus.Deactivated), value: "Deactivated" },
      { label: t(Resources.Form.UserStatus.Onboarding), value: "Onboarding" },
    ],
    size: 150,
  },
];

type Props = {
  data: Array<HunterListDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<HunterListDto>>;
};

export const HuntersTable = ({
  data,
  isRefetching,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const columns = useMemo(() => createColumns(t), [t]);

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("hunter-detail")}/${row.original.userID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
